import React, { useRef } from 'react';
import {
  Badge, Box, Button, ClickAwayListener, Grow, Paper, Popper,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

const CAP_PRESETS = [
  { key: 'micro', label: 'Micro', desc: '<$300M' },
  { key: 'small', label: 'Small', desc: '$300M–$2B' },
  { key: 'mid',   label: 'Mid',   desc: '$2B–$10B' },
  { key: 'large', label: 'Large', desc: '$10B–$200B' },
  { key: 'mega',  label: 'Mega',  desc: '>$200B' },
];

/**
 * Detect which cap preset is active from parsed metrics.
 * Looks for Market Cap conditions and maps them to a preset key.
 */
export function detectCapFromMetrics(metrics) {
  const caps = metrics.filter(m => m.shortName === 'Market Cap');
  if (caps.length === 0) return null;
  const gt = caps.find(m => m.condition === '>');
  const lt = caps.find(m => m.condition === '<');
  const gtVal = gt ? parseFloat(gt.value) : null;
  const ltVal = lt ? parseFloat(lt.value) : null;

  if (ltVal && ltVal <= 300000000 && !gtVal) return 'micro';
  if (gtVal === 300000000 && ltVal === 2000000000) return 'small';
  if (gtVal === 2000000000 && ltVal === 10000000000) return 'mid';
  if (gtVal === 10000000000 && ltVal === 200000000000) return 'large';
  if (gtVal && gtVal >= 200000000000 && !ltVal) return 'mega';
  return null;
}

/**
 * Generate the text lines for a given cap preset key.
 * Returns an array of clause strings.
 */
export function capToTextClauses(capKey) {
  switch (capKey) {
    case 'micro': return ['Market Cap < 300000000'];
    case 'small': return ['Market Cap > 300000000', 'Market Cap < 2000000000'];
    case 'mid':   return ['Market Cap > 2000000000', 'Market Cap < 10000000000'];
    case 'large': return ['Market Cap > 10000000000', 'Market Cap < 200000000000'];
    case 'mega':  return ['Market Cap > 200000000000'];
    default: return [];
  }
}

export default function MarketCapFilter({ selectedCap, onChange, open, onToggle, onClose }) {
  const anchorRef = useRef(null);

  const handleClose = (e) => {
    if (anchorRef.current?.contains(e.target)) return;
    onClose();
  };

  const handleChange = (_e, newKey) => {
    if (newKey !== null) {
      onChange(newKey);
      onClose();
    }
  };

  const active = !!selectedCap;
  const label = selectedCap
    ? CAP_PRESETS.find(p => p.key === selectedCap)?.label + '-Cap'
    : 'Market Cap';

  return (
    <>
      <Badge variant="dot" color="primary" invisible={!active}
        sx={{ '& .MuiBadge-badge': { top: 4, right: 4 } }}
      >
        <Button
          ref={anchorRef}
          size="small"
          variant={active ? 'contained' : 'outlined'}
          disableElevation
          onClick={onToggle}
          endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: '1rem !important' }} />}
          sx={{
            textTransform: 'none', fontSize: '0.82rem', borderRadius: '8px',
            px: 1.5, py: 0.5,
            ...(active ? {
              bgcolor: 'primary.main', color: '#fff',
              '&:hover': { bgcolor: 'primary.dark' },
            } : {
              borderColor: 'rgba(0,0,0,0.15)', color: 'text.secondary',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
            }), minWidth: { sm: 'auto', md: 0 },
          }}
        >
          {label}
        </Button>
      </Badge>

      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start"
        transition disablePortal style={{ zIndex: 20 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
            <Paper sx={{ mt: 0.5, p: 1.5, boxShadow: 4, borderRadius: '8px' }}>
              <ClickAwayListener onClickAway={handleClose}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={selectedCap}
                  onChange={handleChange}
                  sx={{
                    display: 'flex', flexWrap: 'wrap', gap: 0.5,
                    '& .MuiToggleButton-root': {
                      textTransform: 'none', fontSize: '0.8rem', borderRadius: '8px !important',
                      border: '1px solid rgba(0,0,0,0.1)', px: 1.5, py: 0.5,
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                      bgcolor: 'primary.main', color: '#fff',
                      '&:hover': { bgcolor: 'primary.dark' },
                    },
                  }}
                >
                  {CAP_PRESETS.map(p => (
                    <ToggleButton key={p.key} value={p.key}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Box>{p.label}</Box>
                        <Box sx={{ fontSize: '0.68rem', color: 'inherit', opacity: 0.7, lineHeight: 1 }}>{p.desc}</Box>
                      </Box>
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
